import { api } from './api'

const apiVersion = 'v2'

const orderPath = ({ orderId, organisationId }) =>
    `/${apiVersion}/organisations/${organisationId}/webstorequotes/${orderId}`

/**
 * The attachments endpoint of a web store quote, under the API base URL.
 *
 * @param {Object} params
 * @param {string} params.organisationId
 * @param {string} params.orderId
 * @returns {string}
 */
export const orderAttachmentsPath = (args) => `${orderPath(args)}/attachments`

/**
 * The attachments endpoint of one quote item on a web store quote, under the API base URL.
 *
 * @param {Object} params
 * @param {string} params.organisationId
 * @param {string} params.orderId
 * @param {string} params.orderItemId
 * @returns {string}
 */
export const orderItemAttachmentsPath = ({ orderItemId, ...args }) =>
    `${orderPath(args)}/quote-items/${orderItemId}/attachments`

const attachmentsTag = (_result, _error, { orderId }) => [{ type: 'OrderAttachments', id: orderId }]

export const orderAttachmentsApi = api
    .enhanceEndpoints({
        addTagTypes: ['OrderAttachments'],
    })
    .injectEndpoints({
        endpoints: (builder) => ({
            // The web store has no attachments list endpoint; the rows ride on the quote payload.
            // This reads the same URL as getOrder but keeps its own cache entry, so refreshing the
            // rows after an upload or delete doesn't refresh (and re-render) the whole quote page.
            // ponytail: each refresh re-downloads the whole quote; ask for a list endpoint if heavy.
            getOrderAttachments: builder.query({
                query: orderPath,
                transformResponse: (order) => order?.attachments ?? [],
                providesTags: attachmentsTag,
            }),

            deleteOrderAttachment: builder.mutation({
                query: ({ documentId, orderId, organisationId }) => ({
                    url: `${orderAttachmentsPath({ organisationId, orderId })}/${documentId}`,
                    method: 'DELETE',
                }),
                invalidatesTags: attachmentsTag,
            }),

            // Quote item rows ride on getOrderItems, so a delete refreshes that item's entry there.
            deleteOrderItemAttachment: builder.mutation({
                query: ({ documentId, ...args }) => ({
                    url: `${orderItemAttachmentsPath(args)}/${documentId}`,
                    method: 'DELETE',
                }),
                invalidatesTags: (_result, _error, { orderItemId }) => [{ type: 'OrderItems', id: orderItemId }],
            }),
        }),
    })

export const { useDeleteOrderAttachmentMutation, useDeleteOrderItemAttachmentMutation, useGetOrderAttachmentsQuery } =
    orderAttachmentsApi
